import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { IMAGES, EVENTS_PHOTOS } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default values used as fallback if /api/settings hasn't loaded yet or is empty
export const DEFAULT_SETTINGS = {
  phone: "+1 (314) 391-8326",
  email: "laststopbikeshop@proton.me",
  address: "504 N Newstead Ave, St. Louis, MO 63108",
  short_address: "504 N Newstead Ave, St. Louis",
  maps_embed:
    "https://www.google.com/maps?q=504+N+Newstead+Ave,+St.+Louis,+MO+63108&output=embed",
  hours: [
    { day: "Mon", open: "XX:XX", close: "XX:XX", closed: false },
    { day: "Tue", open: "XX:XX", close: "XX:XX", closed: false },
    { day: "Wed", open: "XX:XX", close: "XX:XX", closed: false },
    { day: "Thu", open: "XX:XX", close: "XX:XX", closed: false },
    { day: "Fri", open: "XX:XX", close: "XX:XX", closed: false },
    { day: "Sat", open: "XX:XX", close: "XX:XX", closed: false },
    { day: "Sun", open: "", close: "", closed: true },
  ],
  services: [
    {
      key: "mechanical",
      category: "Mechanical Service",
      items: [
        {
          name: "Single Part Repair / Upgrade",
          price: "$20",
          note: "Quick swap or upgrade of a single component (tube, brake pads, cables, etc.). Parts additional.",
        },
        {
          name: "Adult Bike Safety Inspection & Tune-Up",
          price: "$50",
          note: "Full safety check, gear and brake adjustments, cleaning, and lubrication. Parts additional if needed.",
        },
        {
          name: "Multi-Part Upgrades & Replacements",
          price: "$100",
          note: "Larger jobs covering several components or a deeper overhaul. We detail issues and recommend the best course of action. Parts additional.",
        },
      ],
    },
    {
      key: "clinics",
      category: "Advanced Clinics",
      items: [
        { name: "One on One", price: "$100", note: "Personalized hands-on instruction." },
        { name: "Small Groups", price: "$80/per", note: "Groups of 2–4. On-site clinics & private events arranged." },
      ],
    },
    {
      key: "fitting",
      category: "Fit Service & Comfort Consult",
      items: [
        { name: "Comfort", price: "$100", note: "Dial in comfort and basic positioning." },
        { name: "Complete", price: "$200", note: "Full studio fit — products, positions, deep work." },
      ],
    },
  ],
  images: IMAGES,
  events_photos: EVENTS_PHOTOS,
};

const SettingsContext = createContext({
  settings: DEFAULT_SETTINGS,
  loading: true,
  refresh: async () => {},
});

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings`);
      setSettings({ ...DEFAULT_SETTINGS, ...data });
    } catch (e) {
      console.error("Failed to load site settings, using defaults", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({ settings, loading, refresh }), [settings, loading, refresh]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
